/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row, UncontrolledTooltip } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getAccountingBankAccounts } from 'app/entities/accounting-bank-account/accounting-bank-account.reducer';
import { getEntities as getAccountingTransactions } from 'app/entities/accounting-transaction/accounting-transaction.reducer';
import { createEntity, getEntity, reset, updateEntity } from './accounting-reconciliation.reducer';

export const AccountingReconciliationUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const accountingBankAccounts = useAppSelector(state => state.accountingBankAccount.entities);
  const accountingTransactions = useAppSelector(state => state.accountingTransaction.entities);
  const accountingReconciliationEntity = useAppSelector(state => state.accountingReconciliation.entity);
  const loading = useAppSelector(state => state.accountingReconciliation.loading);
  const updating = useAppSelector(state => state.accountingReconciliation.updating);
  const updateSuccess = useAppSelector(state => state.accountingReconciliation.updateSuccess);

  const handleClose = () => {
    navigate(`/accounting-reconciliation${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getAccountingBankAccounts({}));
    dispatch(getAccountingTransactions({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.startDate !== undefined && typeof values.startDate !== 'number') {
      values.startDate = Number(values.startDate);
    }
    if (values.endDate !== undefined && typeof values.endDate !== 'number') {
      values.endDate = Number(values.endDate);
    }
    if (values.closingBalance !== undefined && typeof values.closingBalance !== 'number') {
      values.closingBalance = Number(values.closingBalance);
    }
    if (values.difference !== undefined && typeof values.difference !== 'number') {
      values.difference = Number(values.difference);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }

    const entity = {
      ...accountingReconciliationEntity,
      ...values,
      account: accountingBankAccounts.find(it => it.id.toString() === values.account?.toString()),
      transactions: mapIdList(values.transactions),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          ...accountingReconciliationEntity,
          account: accountingReconciliationEntity?.account?.id,
          transactions: accountingReconciliationEntity?.transactions?.map(e => e.id.toString()),
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.accountingReconciliation.home.createOrEditLabel" data-cy="AccountingReconciliationCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.accountingReconciliation.home.createOrEditLabel">
              Create or edit a AccountingReconciliation
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="accounting-reconciliation-id"
                  label={translate('tinyMedicRestApp.accountingReconciliation.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingReconciliation.startDate')}
                id="accounting-reconciliation-startDate"
                name="startDate"
                data-cy="startDate"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="startDateLabel">
                <Translate contentKey="tinyMedicRestApp.accountingReconciliation.help.startDate" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingReconciliation.endDate')}
                id="accounting-reconciliation-endDate"
                name="endDate"
                data-cy="endDate"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="endDateLabel">
                <Translate contentKey="tinyMedicRestApp.accountingReconciliation.help.endDate" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingReconciliation.closingBalance')}
                id="accounting-reconciliation-closingBalance"
                name="closingBalance"
                data-cy="closingBalance"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="closingBalanceLabel">
                <Translate contentKey="tinyMedicRestApp.accountingReconciliation.help.closingBalance" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingReconciliation.difference')}
                id="accounting-reconciliation-difference"
                name="difference"
                data-cy="difference"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="differenceLabel">
                <Translate contentKey="tinyMedicRestApp.accountingReconciliation.help.difference" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingReconciliation.reconciled')}
                id="accounting-reconciliation-reconciled"
                name="reconciled"
                data-cy="reconciled"
                check
                type="checkbox"
              />
              <UncontrolledTooltip target="reconciledLabel">
                <Translate contentKey="tinyMedicRestApp.accountingReconciliation.help.reconciled" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingReconciliation.active')}
                id="accounting-reconciliation-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <UncontrolledTooltip target="activeLabel">
                <Translate contentKey="tinyMedicRestApp.accountingReconciliation.help.active" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingReconciliation.cursor')}
                id="accounting-reconciliation-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="cursorLabel">
                <Translate contentKey="tinyMedicRestApp.accountingReconciliation.help.cursor" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingReconciliation.lastUpdated')}
                id="accounting-reconciliation-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="lastUpdatedLabel">
                <Translate contentKey="tinyMedicRestApp.accountingReconciliation.help.lastUpdated" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingReconciliation.microSec')}
                id="accounting-reconciliation-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="microSecLabel">
                <Translate contentKey="tinyMedicRestApp.accountingReconciliation.help.microSec" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingReconciliation.timestampEntry')}
                id="accounting-reconciliation-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="timestampEntryLabel">
                <Translate contentKey="tinyMedicRestApp.accountingReconciliation.help.timestampEntry" />
              </UncontrolledTooltip>
              <ValidatedField
                id="accounting-reconciliation-account"
                name="account"
                data-cy="account"
                label={translate('tinyMedicRestApp.accountingReconciliation.account')}
                type="select"
              >
                <option value="" key="0" />
                {accountingBankAccounts
                  ? accountingBankAccounts.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingReconciliation.transaction')}
                id="accounting-reconciliation-transaction"
                data-cy="transaction"
                type="select"
                multiple
                name="transactions"
              >
                <option value="" key="0" />
                {accountingTransactions
                  ? accountingTransactions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/accounting-reconciliation" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AccountingReconciliationUpdate;
